import React, { memo } from 'react'
import { Avatar, Box, Grid } from '@mui/material'
import { faImage } from '@fortawesome/pro-thin-svg-icons'
import { useTheme } from '@mui/material/styles'
import { Trans } from 'react-i18next'

import { NFTS_PROFILE_DISPATCH } from '../../store/reducers/nfts/nfts'
import CustomDialog from '../shared/CustomDialog/CustomDialog'
import BaseForm from '../shared/BaseForm/BaseForm'
import FileField from '../shared/inputs/FileField'
import {
  transWithArgs,
  validNftSalesFeeRule
} from '../../util/validation.helpers'
import Input from '../shared/inputs/Input'
import { LABEL_TEXT, QB_ERROR_TEXT } from '../../constants/messages'
import Dropdown from '../shared/inputs/Dropdown'
import MultilineField from '../shared/MultilineField/MultilineField'
import TitleWithPopover from '../shared/TitleWithPopover/TitleWithPopover'
import {
  MULTILINE_FIELD_MAX_LENGTH,
  NFT_NAME_MAX_LENGTH,
  NFT_SUPPLY_MAX,
  SALES_FEE_MAX_LENGTH
} from '../../constants/validation'
import {
  buildNftTypeOptions,
  buildYesNoOptions,
  NFT_YES_NO_OPTIONS
} from '../../data/nfts'
import { formatTicks } from '../../util/string.helpers'
import CustomFormLabel from '../shared/forms/customElements/CustomFormLabel'
import { CSS_FLEX_CENTERED, CSS_FLEX_COLUMN } from '../../styles/common'
import FaIcon from '../shared/FaIcon/FaIcon'
import { QB_INPUT_NUMBER } from '../../constants/input'
import Link from '../shared/link/Link'
import { MARKDOWN_URL } from '../../constants/externalResources'

const CreateNftForm = ({
  nftsProfile: {
    error,
    success,
    isProcessing,
    media,
    mediaUrl,
    name: nftName,
    type,
    externalLink,
    description,
    exclusiveContent,
    pointValue,
    supply,
    showNft,
    userSale,
    userTransfer,
    userSalesFee
  },
  tokenProfile: { name, symbol },
  dispatchAction,
  onClose,
  onSubmit,
  title,
  t
}) => {
  const theme = useTheme()
  return (
    <CustomDialog
      open
      submitProps={{
        label: t('nft.create-nft'),
        disabled: isProcessing
      }}
      onSubmit={onSubmit}
      onClose={onClose}
      customCss={{
        pl: 2,
        pr: 2,
        pb: 0
      }}
      paperProps={{
        maxWidth: '650px'
      }}
      body={
        <BaseForm
          title={title}
          fields={
            <Grid container spacing={3}>
              <Grid item xs={12} sx={{ pt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6} sx={CSS_FLEX_COLUMN}>
                    <FileField
                      id={NFTS_PROFILE_DISPATCH.MEDIA}
                      accept='image/*, audio/*, video/*'
                      label={t('nft.image-video-audio')}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.MEDIA]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.MEDIA]
                      )}
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          dispatchAction(
                            NFTS_PROFILE_DISPATCH.MEDIA,
                            e.target.files[0]
                          )
                        }
                      }}
                    />
                    <Input
                      id={NFTS_PROFILE_DISPATCH.NAME}
                      label={t(`nft.name-title`)}
                      value={nftName}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.NAME]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.NAME]
                      )}
                      onChange={(value) =>
                        dispatchAction(NFTS_PROFILE_DISPATCH.NAME, value)
                      }
                      bottomLabel={t(QB_ERROR_TEXT.CHARACTERS_MAX, {
                        max: formatTicks(NFT_NAME_MAX_LENGTH)
                      })}
                      inputProps={{ maxLength: NFT_NAME_MAX_LENGTH }}
                      size='small'
                    />
                    <Dropdown
                      id={NFTS_PROFILE_DISPATCH.TYPE}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={t(LABEL_TEXT.TYPE)}
                          helpText={t('nft.type-desc')}
                        />
                      }
                      options={buildNftTypeOptions(t)}
                      onChange={({ target }) =>
                        dispatchAction(NFTS_PROFILE_DISPATCH.TYPE, target.value)
                      }
                      value={type}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.TYPE]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.TYPE]
                      )}
                      size='small'
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={CSS_FLEX_COLUMN}>
                    <CustomFormLabel>{t('nft.preview')}</CustomFormLabel>
                    {media || mediaUrl ? (
                      <Avatar
                        src={media ? URL.createObjectURL(media) : mediaUrl}
                        sx={{ width: 230, height: 230, borderRadius: '7px' }}
                        variant='square'
                      />
                    ) : (
                      <Box
                        sx={{
                          ...CSS_FLEX_CENTERED,
                          width: '230px',
                          height: '230px',
                          border: '1px solid',
                          borderColor: theme.palette.grey.A400,
                          borderRadius: '7px'
                        }}
                      >
                        <FaIcon
                          icon={faImage}
                          fontSize={120}
                          color={theme.palette.lightGrey.dark}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item xs={6} sx={{ display: 'flex', gap: 2 }}>
                    <Dropdown
                      id={NFTS_PROFILE_DISPATCH.USER_SALE}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={t('nft.user-sale')}
                          helpText={t('nft.user-sale-desc')}
                        />
                      }
                      value={userSale}
                      options={buildYesNoOptions(t)}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.USER_SALE]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.USER_SALE]
                      )}
                      onChange={({ target }) =>
                        dispatchAction(
                          NFTS_PROFILE_DISPATCH.USER_SALE,
                          target.value
                        )
                      }
                      size='small'
                    />
                    <Dropdown
                      id={NFTS_PROFILE_DISPATCH.USER_TRANSFER}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={t('nft.user-transfer')}
                          helpText={t('nft.user-transfer-desc')}
                        />
                      }
                      options={buildYesNoOptions(t)}
                      value={userTransfer}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.USER_TRANSFER]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.USER_TRANSFER]
                      )}
                      onChange={({ target }) =>
                        dispatchAction(
                          NFTS_PROFILE_DISPATCH.USER_TRANSFER,
                          target.value
                        )
                      }
                      size='small'
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      id={NFTS_PROFILE_DISPATCH.EXTERNAL_LINK}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={`${t('nft.external-link')} (${t(
                            'common.optional'
                          )})`}
                          helpText={t('nft.external-link-desc')}
                        />
                      }
                      value={externalLink}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.EXTERNAL_LINK]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.EXTERNAL_LINK]
                      )}
                      onChange={(value) =>
                        dispatchAction(
                          NFTS_PROFILE_DISPATCH.EXTERNAL_LINK,
                          value
                        )
                      }
                      size='small'
                    />
                  </Grid>
                </Grid>
                {userSale === NFT_YES_NO_OPTIONS.yes && (
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Input
                        id={NFTS_PROFILE_DISPATCH.USER_SALES_FEE}
                        label={
                          <TitleWithPopover
                            variant='h6'
                            title={`${t('nft.user-sales-fee')} (%)`}
                            helpText={t('nft.user-sales-fee-desc', { name })}
                          />
                        }
                        type={QB_INPUT_NUMBER}
                        value={userSalesFee}
                        validate={[validNftSalesFeeRule]}
                        errorMessage={transWithArgs(
                          t,
                          error[NFTS_PROFILE_DISPATCH.USER_SALES_FEE]
                        )}
                        successMsg={transWithArgs(
                          t,
                          success[NFTS_PROFILE_DISPATCH.USER_SALES_FEE]
                        )}
                        onChange={(value) =>
                          dispatchAction(
                            NFTS_PROFILE_DISPATCH.USER_SALES_FEE,
                            value
                          )
                        }
                        inputProps={{ maxLength: SALES_FEE_MAX_LENGTH }}
                        size='small'
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <MultilineField
                      id={NFTS_PROFILE_DISPATCH.DESCRIPTION}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={`${t(LABEL_TEXT.DESCRIPTION)} (${t(
                            'common.optional'
                          )})`}
                          helpText={
                            <Trans i18nKey='nft.description-desc'>
                              <Link
                                isOutside
                                label={t('common.markdown')}
                                to={MARKDOWN_URL}
                              />
                            </Trans>
                          }
                        />
                      }
                      value={description}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.DESCRIPTION]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.DESCRIPTION]
                      )}
                      onChange={(value) =>
                        dispatchAction(NFTS_PROFILE_DISPATCH.DESCRIPTION, value)
                      }
                      inputProps={{ maxLength: MULTILINE_FIELD_MAX_LENGTH }}
                      t={t}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MultilineField
                      id={NFTS_PROFILE_DISPATCH.EXCLUSIVE_CONTENT}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={`${t('nft.exclusive-content')} (${t(
                            'common.optional'
                          )})`}
                          helpText={
                            <Trans i18nKey='nft.exclusive-content-desc'>
                              <Link
                                isOutside
                                label={t('common.markdown')}
                                to={MARKDOWN_URL}
                              />
                            </Trans>
                          }
                        />
                      }
                      value={exclusiveContent}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.EXCLUSIVE_CONTENT]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.EXCLUSIVE_CONTENT]
                      )}
                      onChange={(value) =>
                        dispatchAction(
                          NFTS_PROFILE_DISPATCH.EXCLUSIVE_CONTENT,
                          value
                        )
                      }
                      placeholder={t('nft.exclusive-content-placeholder')}
                      inputProps={{ maxLength: MULTILINE_FIELD_MAX_LENGTH }}
                      t={t}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item md={6} xs={12}>
                    <Input
                      id={NFTS_PROFILE_DISPATCH.POINT_VALUE}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={`${t('nft.point-value')} (${t(
                            'common.optional'
                          )})`}
                          helpText={t('nft.point-value-desc', {
                            symbol,
                            name
                          })}
                        />
                      }
                      value={pointValue}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.POINT_VALUE]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.POINT_VALUE]
                      )}
                      onChange={(value) =>
                        dispatchAction(NFTS_PROFILE_DISPATCH.POINT_VALUE, value)
                      }
                      size='small'
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Input
                      id={NFTS_PROFILE_DISPATCH.SUPPLY}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={t('nft.supply')}
                          helpText={t('nft.supply-desc')}
                        />
                      }
                      type={QB_INPUT_NUMBER}
                      value={supply}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.SUPPLY]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.SUPPLY]
                      )}
                      onChange={(value) =>
                        dispatchAction(NFTS_PROFILE_DISPATCH.SUPPLY, value)
                      }
                      placeholder={t('nft.supply-placeholder')}
                      bottomLabel={t(QB_ERROR_TEXT.MAX, {
                        max: formatTicks(NFT_SUPPLY_MAX)
                      })}
                      size='small'
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Dropdown
                      id={NFTS_PROFILE_DISPATCH.SHOW_NFT}
                      label={
                        <TitleWithPopover
                          variant='h6'
                          title={t('nft.show-nft')}
                          helpText={t('nft.show-nft-desc')}
                        />
                      }
                      options={buildYesNoOptions(t)}
                      value={showNft}
                      errorMessage={transWithArgs(
                        t,
                        error[NFTS_PROFILE_DISPATCH.SHOW_NFT]
                      )}
                      successMsg={transWithArgs(
                        t,
                        success[NFTS_PROFILE_DISPATCH.SHOW_NFT]
                      )}
                      onChange={({ target }) =>
                        dispatchAction(
                          NFTS_PROFILE_DISPATCH.SHOW_NFT,
                          target.value
                        )
                      }
                      size='small'
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          }
        />
      }
      t={t}
    />
  )
}

export default memo(CreateNftForm)
